// Shared public services hook.
// The deployed database is the only runtime source of truth. The static
// baseline remains in the repository only for backend seeding/development.
import { useEffect, useMemo, useState } from 'react';
import {
  getPublic,
  getPublicCacheVersion,
  readPublicCache,
  toAssetUrl,
  writePublicCache,
} from '@/data/runtimeApi';

const normalizeService = (service) => ({
  ...service,
  bgImage: toAssetUrl(service.bgImage),
  hubBanner: toAssetUrl(service.hubBanner),
  homeBody: Array.isArray(service.homeBody) ? service.homeBody : [],
});

const cachedServices = readPublicCache('services');
let _cache = Array.isArray(cachedServices) ? cachedServices : null;
let _cacheVersion = getPublicCacheVersion();
let _promise = null;

async function fetchServices() {
  if (_promise) return _promise;

  _promise = (async () => {
    const { data } = await getPublic('/servicios');

    if (!Array.isArray(data?.items)) {
      throw new Error('La API pública de servicios devolvió un formato inválido.');
    }

    const services = data.items.map(normalizeService);
    _cache = services;
    _cacheVersion = getPublicCacheVersion();
    writePublicCache('services', services);
    return services;
  })();

  try {
    return await _promise;
  } finally {
    _promise = null;
  }
}

export function useServices() {
  const currentCacheVersion = getPublicCacheVersion();
  if (_cacheVersion !== currentCacheVersion) {
    _cache = null;
    _cacheVersion = currentCacheVersion;
  }

  const [services, setServices] = useState(() => _cache || []);
  const [loading, setLoading] = useState(() => _cache === null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;

    if (_cache === null) setLoading(true);
    setError(null);

    fetchServices()
      .then((list) => {
        if (!alive) return;
        setServices(list);
      })
      .catch((requestError) => {
        if (!alive) return;
        console.error('[public services] No se pudieron cargar los servicios administrados.', requestError);
        setError(requestError);
        if (_cache) setServices(_cache);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  const bySlug = useMemo(() => {
    const map = {};
    services.forEach((service) => {
      if (service?.slug) map[service.slug] = service;
    });
    return map;
  }, [services]);

  return {
    services,
    loading,
    error,
    hasData: services.length > 0,
    getServiceBySlug: (slug) => bySlug[slug],
  };
}
